import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { useUniversalSearch, SearchResult } from '@/hooks/useUniversalSearch';
import { useSearchContext } from '@/hooks/useSearchContext';
import { SearchResultItem } from './SearchResultItem';
import { SearchContextToggle, SearchMode } from './SearchContextToggle';
import { Loader2, Sparkles } from 'lucide-react';

interface UniversalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAskPoco?: (query: string) => void;
}

function looksLikeQuestion(query: string): boolean {
  const q = query.trim().toLowerCase();
  const starters = ['who', 'what', 'when', 'where', 'why', 'how', 'which', 'is', 'are', 'can', 'did', 'does', 'do', 'will', 'should', 'could', 'would', 'tell me', 'show me', 'find'];
  return q.endsWith('?') || starters.some(s => q.startsWith(s + ' '));
}

export function UniversalSearchDialog({
  open,
  onOpenChange,
  onAskPoco,
}: UniversalSearchDialogProps) {
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('local');
  const navigate = useNavigate();
  const context = useSearchContext();

  // Reset search mode when context changes
  useEffect(() => {
    if (context?.clientId) {
      setSearchMode('local');
    } else {
      setSearchMode('global');
    }
  }, [context?.clientId]);

  // Clear query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Determine the clientId to use for search
  const clientIdForSearch = searchMode === 'local' ? context?.clientId : null;

  const { data: results, isLoading, error } = useUniversalSearch(query, {
    clientId: clientIdForSearch,
    enabled: open,
  });

  const handleSelect = useCallback(
    (result: SearchResult) => {
      onOpenChange(false);
      navigate(result.url);
    },
    [navigate, onOpenChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Close on Escape
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  const showResults = query.trim().length >= 2;
  const hasResults = results && results.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <div onKeyDown={handleKeyDown}>
        <CommandInput
          placeholder="Search clients, meetings, projects, tasks..."
          value={query}
          onValueChange={setQuery}
        />
        <SearchContextToggle
          mode={searchMode}
          onModeChange={setSearchMode}
          contextLabel={context?.clientName}
        />
        <CommandList className="max-h-[400px]">
          {isLoading && showResults && (
            <div className="py-6 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && showResults && !hasResults && (
            <CommandEmpty>
              No results found for "{query}".
              {searchMode === 'local' && context?.clientName && (
                <span className="block text-xs mt-1">
                  Try searching "Everywhere" for more results.
                </span>
              )}
            </CommandEmpty>
          )}

          {!isLoading && error && (
            <div className="py-6 text-center text-sm text-destructive">
              Error searching. Please try again.
            </div>
          )}

          {!showResults && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search
            </div>
          )}

          {showResults && looksLikeQuestion(query) && onAskPoco && (
            <CommandGroup heading="Ask Poco">
              <CommandItem
                onSelect={() => {
                  onOpenChange(false);
                  onAskPoco(query);
                }}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                <div>
                  <div className="font-medium">Ask Poco: &quot;{query}&quot;</div>
                  <div className="text-xs text-muted-foreground">Get an AI-powered answer</div>
                </div>
              </CommandItem>
            </CommandGroup>
          )}

          {hasResults && (
            <CommandGroup heading="Results">
              {results.map((result, index) => (
                <SearchResultItem
                  key={`${result.entity_type}-${result.id}-${index}`}
                  result={result}
                  onSelect={() => handleSelect(result)}
                />
              ))}
            </CommandGroup>
          )}
        </CommandList>

        {/* Footer with keyboard hint */}
        <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
              Enter
            </kbd>{' '}
            to select
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
              Esc
            </kbd>{' '}
            to close
          </span>
        </div>
      </div>
    </CommandDialog>
  );
}
